<template>
  <div>
    <vue-final-modal
      :value="value"
      name="english-profile-ai-dialog"
      :classes="['english-profile-ai-dialog__container']"
      :content-class="[
        'english-profile-ai-dialog__content',
        'v--modal-box',
        'v--modal'
      ]"
      :click-to-close="false"
      :esc-to-close="false"
      :lock-scroll="true"
      attach="body"
      @input="emit('input', $event)"
    >
      <div class="modal-inner">
        <div class="modal-header">
          <h3>✨ 英文Profile を AIで作成</h3>
          <button class="close-btn" type="button" @click="closeDialog">
            ×
          </button>
        </div>
        <p class="modal-lead">
          キャリアの基本情報を入力すると、英文レジュメに最適化された Profile
          セクションを生成します
        </p>

        <!-- 入力フォーム -->
        <div v-if="!generatedText" class="modal-body">
          <div class="edit-form">
            <div class="edit-form-item">
              <div class="item-name">
                あなたの強み・コアスキル <span class="required">必須</span>
              </div>
              <el-input
                v-model="form.strength"
                type="textarea"
                :autosize="{ minRows: 3, maxRows: 4 }"
                placeholder="例: クラウドインフラ設計、チームマネジメント、データ分析"
                maxlength="500"
              />
            </div>

            <div class="edit-form-item">
              <div class="item-name">
                経験年数 <span class="required">必須</span>
              </div>
              <div class="inline-input">
                <el-input
                  v-model.number="form.experienceYears"
                  placeholder="例: 8"
                  class="input-xs"
                />
                <span class="unit">年</span>
              </div>
            </div>

            <div class="edit-form-item">
              <div class="item-name">
                希望業界 <span class="optional">任意</span>
              </div>
              <el-input
                v-model="form.targetIndustry"
                placeholder="例: SaaS、フィンテック、ヘルスケア"
                maxlength="200"
              />
            </div>

            <div class="edit-form-item">
              <div class="item-name">
                希望職種 <span class="optional">任意</span>
              </div>
              <el-input
                v-model="form.targetPosition"
                placeholder="例: シニアバックエンドエンジニア、プロダクトマネージャー"
                maxlength="200"
              />
            </div>

            <div class="edit-form-item">
              <div class="item-name">
                具体的な実績 <span class="optional">任意</span>
              </div>
              <el-input
                v-model="form.achievements"
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 5 }"
                placeholder="例: サーバーコストを30%削減、12人のチームをリード、月間500万トランザクション処理基盤を設計"
                maxlength="1000"
              />
            </div>
          </div>

          <div class="action-area">
            <el-button
              class="generate-btn"
              :loading="isGenerating"
              :disabled="!canGenerate"
              @click="generateProfile"
            >
              <span v-if="!isGenerating">✨ 生成する</span>
              <span v-else>🌀 AIが Profile を生成中...</span>
            </el-button>
            <p v-if="!canGenerate" class="hint">
              「強み」と「経験年数」は入力必須です
            </p>
          </div>

          <div v-if="errorMessage" class="error-message">
            ⚠️ {{ errorMessage }}
          </div>
        </div>

        <!-- 生成結果 -->
        <div v-else class="modal-body result-area">
          <h4>生成された Profile</h4>
          <div class="generated-preview">{{ generatedText }}</div>
          <div v-if="!isPrime && truncated" class="prime-cta">
            <p>
              🔒 続きを見るには <strong>ヤギプライム</strong> 会員登録が必要です
            </p>
            <p class="small">無料プランは1日1回・先頭100字までです</p>
            <el-button class="prime-btn" @click="openYagiprimeDialog">
              ⭐ ヤギプライムを見る
            </el-button>
          </div>
          <div class="action-area">
            <el-button class="adopt-btn" @click="adoptProfile">
              ✓ この Profile を使う
            </el-button>
            <el-button :disabled="isGenerating" @click="regenerate">
              ↻ 別案を生成
            </el-button>
            <el-button type="text" @click="resetDialog">入力に戻る</el-button>
          </div>
          <p v-if="usageCount !== null" class="usage-info">
            本日の使用回数: {{ usageCount }} / {{ maxUsageCount }} 回
          </p>
        </div>
      </div>
    </vue-final-modal>

    <english-profile-replace-confirm-dialog
      :value="replaceConfirmVisible"
      @input="replaceConfirmVisible = $event"
      @replace="confirmReplace"
      @append="appendInsteadOfReplace"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue'
import EnglishProfileReplaceConfirmDialog from '@/components/yagiprime/EnglishProfileReplaceConfirmDialog.vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'

type AiEnglishProfileResponse = {
  success?: boolean
  message?: string
  is_prime?: boolean
  usage_count?: number | null
  max_usage_count?: number | null
}

type AxiosErrorLike = {
  response?: {
    status?: number
    data?: { message?: string }
  }
}

type YagiprimePaymentService = {
  useFeature: (
    host: unknown,
    feature: string,
    hooks: {
      onSuccess: () => void
      onAlreadyPremium?: () => void
      onCancel?: () => void
    }
  ) => void
}

const props = withDefaults(
  defineProps<{
    value: boolean
    currentValue?: string
  }>(),
  {
    currentValue: ''
  }
)

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'adopt-profile', text: string): void
  (e: 'update:generating', value: boolean): void
}>()

const { vm, varuna, yagiprimePaymentService } = useHostServices()
const { user } = useAuth()
const axiosVm = getCurrentInstance()!.proxy as unknown as {
  $axios: {
    post: (
      url: string,
      data: FormData
    ) => Promise<{ data: AiEnglishProfileResponse }>
  }
}

const paymentService = yagiprimePaymentService as YagiprimePaymentService

const isGenerating = ref(false)
const form = reactive({
  strength: '',
  experienceYears: '' as number | string,
  targetIndustry: '',
  targetPosition: '',
  achievements: ''
})
const generatedText = ref('')
const truncated = ref(false)
const isPrime = ref(false)
const usageCount = ref<number | null>(null)
const maxUsageCount = ref<number | null>(null)
const errorMessage = ref('')
const replaceConfirmVisible = ref(false)

const canGenerate = computed(
  () =>
    !!form.strength &&
    form.experienceYears !== '' &&
    Number(form.experienceYears) >= 0
)

watch(isGenerating, (value) => {
  emit('update:generating', value)
})

watch(
  () => props.value,
  (visible) => {
    if (visible) {
      errorMessage.value = ''
    }
  }
)

function closeDialog() {
  emit('input', false)
}

function resetDialog() {
  generatedText.value = ''
  truncated.value = false
  errorMessage.value = ''
}

function generateProfile() {
  if (!canGenerate.value) return

  paymentService.useFeature(vm, 'english-profile-ai', {
    onSuccess: () => executeGenerate(),
    onAlreadyPremium: () => executeGenerate()
  })
}

function executeGenerate() {
  isGenerating.value = true
  errorMessage.value = ''

  const formData = new FormData()
  formData.append('uuid', user.value?.uid ?? '')
  formData.append('strength', form.strength)
  formData.append('experience_years', String(form.experienceYears))
  formData.append('target_industry', form.targetIndustry || '')
  formData.append('target_position', form.targetPosition || '')
  formData.append('achievements', form.achievements || '')

  axiosVm.$axios
    .post(varuna.endPoints.aiEnglishProfile, formData)
    .then((res) => {
      const data = res.data
      if (data && data.success) {
        generatedText.value = data.message || ''
        isPrime.value = !!data.is_prime
        usageCount.value =
          data.usage_count === undefined ? null : data.usage_count
        maxUsageCount.value =
          data.max_usage_count === undefined ? null : data.max_usage_count
        truncated.value = !isPrime.value && generatedText.value.length >= 95
      } else {
        errorMessage.value = (data && data.message) || 'AI生成に失敗しました'
      }
    })
    .catch((err: AxiosErrorLike) => {
      const status = err.response && err.response.status
      const msg = err.response && err.response.data && err.response.data.message
      if (status === 404) {
        errorMessage.value = msg || 'AIを利用するには会員登録が必要です'
      } else if (status === 400) {
        errorMessage.value = msg || 'AIの使用回数が上限に達しました'
      } else {
        errorMessage.value = msg || 'AI生成中にエラーが発生しました'
      }
    })
    .finally(() => {
      isGenerating.value = false
    })
}

function regenerate() {
  generatedText.value = ''
  generateProfile()
}

function adoptProfile() {
  if (props.currentValue && props.currentValue.trim()) {
    replaceConfirmVisible.value = true
    return
  }
  emit('adopt-profile', generatedText.value)
  closeDialog()
  resetDialog()
}

function confirmReplace() {
  emit('adopt-profile', generatedText.value)
  replaceConfirmVisible.value = false
  closeDialog()
  resetDialog()
}

function appendInsteadOfReplace() {
  const merged =
    (props.currentValue || '').trim() + '\n\n' + generatedText.value
  emit('adopt-profile', merged)
  replaceConfirmVisible.value = false
  closeDialog()
  resetDialog()
}

function openYagiprimeDialog() {
  emitter.emit('open-yagiprime-payment-dialog')
}
</script>

<style lang="scss">
@import '@/assets/scss/format-edit/vfm-attach-body-fixed';
@include vfm-root-fixed-when-has('.english-profile-ai-dialog__container');
</style>

<style
  lang="scss"
  src="@/assets/scss/yagiprime/english-profile-ai-dialog.scss"
></style>
